<template>
  <div class="float-assign-form">
    <form @submit.prevent="startShift" class="modern-form">
      <div class="form-group mb-3">
        <div class="form-floating">
          <input 
            type="text" 
            class="form-control custom-input"
            v-model="assignedTo" 
            id="assignedToInput"
            placeholder="Assigned To" 
            required 
            readonly
          >
          <label for="assignedToInput">Assigned To</label>
        </div>
      </div>

      <div class="form-group mb-4">
        <div class="form-floating">
          <input 
            type="number" 
            step="0.01" 
            class="form-control custom-input"
            v-model="floatAmount" 
            id="floatAmountInput"
            placeholder="Float Amount"
            required
          >
          <label for="floatAmountInput">Float Amount</label>
        </div>
      </div>

      <div class="text-center">
        <button 
          :disabled="loading" 
          type="submit" 
          class="float-button btn-primary-lightning"
        >
          <span class="button-text">Start Shift</span>
          <span v-if="loading" class="spinner-grow spinner-grow-sm ms-2" role="status" aria-hidden="true"></span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: "FloatAssignForm",
  data() {
    return {
      assignedTo: '',
      floatAmount: 0,
      supervisorPassword: 'Auto',
      allowed: true,
      users: [],
      loading: false,
    }
  },
  beforeMount() {
    this.checkFloat()
  },
  computed: {
    curUser() {
      return this.$store.getters.getCurrentUser;
    },
    set() {
      return this.$store.getters.getDeviceSettings
    },
  },
  methods: {
    async checkFloat() {
      this.loading = true;
      return this.$store.dispatch('checkUserFloat')
        .then(async (res) => {
          if (typeof res === 'string') {
            this.assignedTo = res
            return this.$store.dispatch('getAutoFloatByGroup').then(res => this.floatAmount = res)
          }
        }).finally(() => this.loading = false)
    },

    setUserFloat(user) {
      return this.$store.dispatch('checkFloat', {
        user: user,
        device: this.set.PC
      }).then(() => {
        return this.$store.commit('setUserFloat', {
          user: user,
          device: this.set.PC,
          supervisor: 'Auto',
          float: this.floatAmount
        });
      })
    },

    startShift() {
      this.loading = true;
      this.setFloat()
        .finally(() => this.loading = false);
    },

    setFloat() {
      return this.$store.dispatch('assignFloat', {
        user: this.assignedTo,
        amount: Number(this.floatAmount),
        authorisedBy: this.supervisorPassword,
      }).then(async (assigned) => {
        if (assigned) {
          try {
            const { Storage } = await import('@/services/Storage');
            if (this.assignedTo) await Storage.clearFloatPending(this.assignedTo);
          } catch (e) {}
          
          // Emit event to parent instead of routing directly
          this.$emit('float-assigned');
          // Fallback routing if parent doesn't handle the event
          setTimeout(() => {
            if (this.$route.name !== 'pos') {
              this.$router.push({ name: 'pos' });
            }
          }, 100);
        }
      })
    },
  }
}
</script>

<style lang="scss" scoped>
.float-assign-form {
  width: 100%;
  animation: fadeIn 0.5s ease forwards;
  opacity: 1; /* Start visible for Electron compatibility */
}

.modern-form {
  padding: 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.custom-input {
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  height: 56px;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:focus {
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.25);
    border-color: var(--accent-electric-blue);
    outline: none;
  }

  &:hover:not(:focus) {
    border-color: var(--accent-electric-blue);
  }

  &:read-only {
    background-color: rgba(var(--text-secondary-rgb, 100, 116, 139), 0.1);
    cursor: not-allowed;
  }
}

.form-floating > label {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  pointer-events: none;
}

.float-button {
  width: 100%;
  max-width: 350px;
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  box-shadow: 0px 2px 8px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0px 4px 16px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.8s ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    width: 1rem;
    height: 1rem;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dark mode specific adjustments */
[data-theme='dark'] {
  .custom-input {
    background-color: rgba(255, 255, 255, 0.05);
    
    &:read-only {
      background-color: rgba(255, 255, 255, 0.03);
    }
  }
}

/* Responsive */
@media (max-width: 768px) {
  .float-button {
    max-width: 100%;
    font-size: 1rem;
  }
}
</style>
